/**
 * TalentLens AI - Job Detail Page
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import api from '../lib/api';
import { useAuthStore, canCreateJob } from '../store/auth';
import RichTextEditor from '../components/RichTextEditor';

export default function JobDetailPage() {
  const params = useParams({ strict: false }) as { jobId: string };
  const jobId = params.jobId;
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [showEditModal, setShowEditModal] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['job', jobId],
    queryFn: () => api.getJob(jobId),
  });

  const { data: versions } = useQuery({
    queryKey: ['job-versions', jobId],
    queryFn: () => api.getJobVersions(jobId),
  });

  const rollbackMutation = useMutation({
    mutationFn: (version: number) => api.rollbackJobVersion(jobId, version),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job', jobId] });
      queryClient.invalidateQueries({ queryKey: ['job-versions', jobId] });
    },
  });

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-gray-200 rounded w-64" />
        <div className="bg-white rounded-xl shadow-sm p-6 h-96" />
      </div>
    );
  }

  if (error || !data?.data) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">Failed to load job</p>
        <Link to="/jobs" className="text-primary-600 hover:underline">Back to Jobs</Link>
      </div>
    );
  }

  const job = data.data;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <Link to="/jobs" className="text-sm text-gray-500 hover:text-gray-700 mb-2 inline-block">
            &larr; Back to Jobs
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">{job.title}</h1>
          <div className="flex items-center gap-3 mt-2">
            <PriorityBadge priority={job.priority} />
            {!job.isActive && (
              <span className="px-2 py-1 text-sm bg-gray-100 text-gray-600 rounded">Inactive</span>
            )}
            <span className="text-sm text-gray-500">Version {job.version}</span>
          </div>
        </div>

        <div className="flex gap-2">
          {canCreateJob(user) && (
            <button
              onClick={() => setShowEditModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            >
              <EditIcon className="w-5 h-5" />
              Edit Job
            </button>
          )}
          <Link
            to="/match/$jobId"
            params={{ jobId: job.id }}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            <MatchIcon className="w-5 h-5" />
            Find Candidates
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Description</h2>
            <div
              className="prose prose-sm max-w-none text-gray-600"
              dangerouslySetInnerHTML={{ __html: job.description }}
            />
          </div>

          {/* Version History */}
          {versions?.data?.versions && versions.data.versions.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Version History</h2>
              <div className="space-y-4">
                {versions.data.versions.map((v: any) => (
                  <VersionItem
                    key={v.version}
                    version={v}
                    isCurrent={v.version === job.version}
                    jobId={jobId}
                    onRollback={(version) => rollbackMutation.mutate(version)}
                    isRollingBack={rollbackMutation.isPending}
                    canRollback={canCreateJob(user)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Details */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Details</h2>
            <dl className="space-y-3 text-sm">
              {job.team && (
                <div>
                  <dt className="text-gray-500">Team</dt>
                  <dd className="font-medium text-gray-900">{job.team}</dd>
                </div>
              )}
              <div>
                <dt className="text-gray-500">Owner</dt>
                <dd className="font-medium text-gray-900">{job.owner?.name || 'Unassigned'}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Created</dt>
                <dd className="font-medium text-gray-900">
                  {new Date(job.createdAt).toLocaleDateString()}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">Last Updated</dt>
                <dd className="font-medium text-gray-900">
                  {new Date(job.updatedAt).toLocaleDateString()}
                </dd>
              </div>
            </dl>
          </div>

          {/* Actions */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Actions</h2>
            <div className="space-y-2">
              <Link
                to="/match/$jobId"
                params={{ jobId: job.id }}
                className="block w-full text-center px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200"
              >
                Find Matching Candidates
              </Link>
              {canCreateJob(user) && (
                <button
                  onClick={() => setShowEditModal(true)}
                  className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Edit Job
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <EditJobModal
          job={job}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false);
            queryClient.invalidateQueries({ queryKey: ['job', jobId] });
            queryClient.invalidateQueries({ queryKey: ['job-versions', jobId] });
          }}
        />
      )}
    </div>
  );
}

interface VersionItemProps {
  version: any;
  isCurrent: boolean;
  jobId: string;
  onRollback: (version: number) => void;
  isRollingBack: boolean;
  canRollback: boolean;
}

function VersionItem({ version, isCurrent, onRollback, isRollingBack, canRollback }: VersionItemProps) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={`border-l-2 pl-4 ${isCurrent ? 'border-primary-500' : 'border-gray-200'}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-900">Version {version.version}</span>
          {isCurrent && (
            <span className="px-2 py-0.5 text-xs bg-primary-100 text-primary-700 rounded">Current</span>
          )}
          <span className="text-xs text-gray-500">
            {new Date(version.updated_at).toLocaleString()}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {!isCurrent && canRollback && (
            <button
              onClick={() => {
                if (confirm(`Rollback to version ${version.version}? This will create a new version.`)) {
                  onRollback(version.version);
                }
              }}
              disabled={isRollingBack}
              className="text-sm text-primary-600 hover:text-primary-700 disabled:opacity-50"
            >
              {isRollingBack ? 'Rolling back...' : 'Rollback'}
            </button>
          )}
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            {expanded ? 'Hide' : 'Show'} details
          </button>
        </div>
      </div>
      <p className="text-sm text-gray-600 mt-1">{version.changes}</p>
      {version.updated_by && (
        <p className="text-xs text-gray-500">by {version.updated_by.name}</p>
      )}
      {expanded && (
        <div className="mt-3 p-3 bg-gray-50 rounded-lg">
          <p className="text-sm font-medium text-gray-700 mb-1">Title:</p>
          <p className="text-sm text-gray-600 mb-3">{version.title}</p>
          <p className="text-sm font-medium text-gray-700 mb-1">Description:</p>
          <div
            className="text-sm text-gray-600 prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: version.description }}
          />
        </div>
      )}
    </div>
  );
}

interface EditJobModalProps {
  job: any;
  onClose: () => void;
  onSuccess: () => void;
}

function EditJobModal({ job, onClose, onSuccess }: EditJobModalProps) {
  const [title, setTitle] = useState(job.title);
  const [description, setDescription] = useState(job.description);
  const [priority, setPriority] = useState(job.priority);
  const [team, setTeam] = useState(job.team || '');
  const [changes, setChanges] = useState('');

  const updateMutation = useMutation({
    mutationFn: () => api.updateJob(job.id, { title, description, priority, team: team || undefined, changes }),
    onSuccess,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Edit Job</h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                minLength={3}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <RichTextEditor
                content={description}
                onChange={setDescription}
                placeholder="Job description, requirements, responsibilities..."
                minHeight="250px"
              />
              <p className="text-xs text-gray-500 mt-1">Use the toolbar to format text with headings, lists, and more</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Team</label>
                <input
                  type="text"
                  value={team}
                  onChange={(e) => setTeam(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Change Summary</label>
              <input
                type="text"
                value={changes}
                onChange={(e) => setChanges(e.target.value)}
                placeholder="Brief description of what changed"
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={updateMutation.isPending}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function PriorityBadge({ priority }: { priority: string }) {
  const styles: Record<string, string> = {
    high: 'bg-red-100 text-red-700',
    medium: 'bg-yellow-100 text-yellow-700',
    low: 'bg-green-100 text-green-700',
  };

  return (
    <span className={`px-2 py-1 text-sm font-medium rounded capitalize ${styles[priority] || 'bg-gray-100 text-gray-700'}`}>
      {priority}
    </span>
  );
}

function MatchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  );
}

function EditIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
    </svg>
  );
}
